import { useEffect, useMemo, useState } from "react"
import { generatePath, Link, useNavigate, useParams } from "react-router-dom"
import { observer } from "mobx-react"
import { Alert, theme, Typography } from "antd"
import { choose } from "choose-matching"
import { QRCodeSVG } from "qrcode.react"

import { ChooseCurrency } from "@src/app/components/ChooseCurrency"
import { CopyBlock } from "@src/app/components/CopyBlock"
import { DepositEmptyHistory } from "@src/app/components/icons/depositEmptyHistory"
import { LoadingBlock } from "@src/app/components/LoadingBlock"
import { Navigation } from "@src/app/components/navigation"
import { OptionallyVisible } from "@src/app/components/OptionallyVisible"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { paths } from "@src/app/paths"
import { transactionsTopUpStore } from "@src/app/transactionsTopUpStore"
import { catchErrors } from "@src/shared/api"
import {
  NetworkAll,
  NetworkCrypto,
  SymbolAll,
  SymbolCrypto,
} from "@src/shared/api/generated"
import { getCurrencyIcon } from "@src/shared/getCurrencyIcon"
import { DepositListItem } from "./depositListItem"
import css from "./index.module.css"
import { DepositStore } from "./store"

const { Paragraph, Title } = Typography

export const DepositPage = observer(() => {
  const { network, symbol } = useParams()
  const { token } = theme.useToken()
  const [isAvailable, setIsAvailable] = useState(true)
  const [chosenCurrency, setChosenCurrency] = useState({ network, symbol })
  const currencies = GlobalCurrenciesCollection()
  const { topUps } = transactionsTopUpStore
  const [store] = useState(new DepositStore())
  const { address, getAddress, isLoading: isDepositLoading } = store
  const navigate = useNavigate()

  const handleChange = (value: string) => {
    setIsAvailable(true)

    const [symbol, network] = value.split(" ")

    setChosenCurrency({ network, symbol })

    getAddress(network as NetworkCrypto, symbol as SymbolCrypto).catch(
      (error) => {
        catchErrors(error)

        setIsAvailable(false)
      },
    )

    navigate(
      generatePath(paths.deposit, {
        network: network as string,
        symbol: symbol as string,
      }),
    )
  }

  const currencyFull = useMemo(() => {
    return currencies.getBySymbolNetwork({
      network: chosenCurrency.network as NetworkAll,
      symbol: chosenCurrency.symbol as SymbolAll,
    })
  }, [chosenCurrency])

  const icon = getCurrencyIcon({ network: network!, symbol: symbol! })

  useEffect(() => {
    getAddress(network as NetworkCrypto, symbol as SymbolCrypto).catch(
      (error) => {
        catchErrors(error)

        setIsAvailable(false)
      },
    )
  }, [])

  const deposits = useMemo(() => {
    return topUps
      .filter((item) => item.network === network && item.symbol === symbol)
      .slice(0, 6)
  }, [topUps, network, symbol])

  return (
    <div className={css.container}>
      <Navigation title="Deposit" url={paths.balances} />
      <div className={css.grid}>
        <div className={css.formWrapper}>
          <ChooseCurrency
            currencies={currencies.crypto}
            network={network!}
            onChange={handleChange}
            symbol={symbol!}
          />
          {choose(
            [isDepositLoading, <LoadingBlock />],
            [
              !isAvailable,
              <Title
                level={3}
                style={{ marginTop: "30px", textAlign: "center" }}
              >{`${chosenCurrency.symbol} (${chosenCurrency.network}) deposit is currently unavailable. Please try later`}</Title>,
            ],
            [
              Boolean(address),
              <>
                <QRCodeSVG
                  fgColor={token.colorPrimary}
                  id="GENERATED_ID_DEPOSIT_QR"
                  imageSettings={{
                    excavate: false,
                    height: 36,
                    src: icon,
                    width: 42,
                    x: undefined,
                    y: undefined,
                  }}
                  size={200}
                  value={address as string}
                />
              </>,
            ],
          )}
        </div>
        <div className={css.table}>
          <div className={css.tableHeader}>
            <span className={css.description}>History</span>
            <Link
              id="GENERATED_ID_DEPOSIT_LINK_ALL_TRANSACTIONS_1"
              to={`${paths.transactions}?tab=tu`}
            >
              All Transactions
            </Link>
          </div>
          <OptionallyVisible visible={Boolean(deposits.length)}>
            <ul className={css.depositList}>
              {deposits.map((item) => (
                <DepositListItem item={item} key={item.id} />
              ))}
            </ul>
          </OptionallyVisible>
          <OptionallyVisible visible={Boolean(!deposits.length)}>
            <div className={css.emptyBlock}>
              <DepositEmptyHistory />
              <Paragraph className={css.paragraph}>
                This will show your recent transactions
              </Paragraph>
            </div>
          </OptionallyVisible>
        </div>
      </div>
      <CopyBlock
        content={address as string}
        message="address copied"
        style={{ maxWidth: "40%" }}
      />
      <div className={css.textWrapper}>
        {`Only send ${currencyFull?.symbol_alias ?? symbol} on the ${network} network to this address or you will lose
        your deposit`}
      </div>
      <Alert
        className={css.addressWarning}
        description={
          <>
            <strong>Check the address before every top-up.</strong> Deposit
            addresses can change. Always copy the current address from this page
            and never reuse one from your notes, history, or a previous
            transfer.
          </>
        }
        style={{ padding: 10 }}
        type="warning"
      />
    </div>
  )
})
